import { initialProducts } from '@/data/catalog';
import { storage } from '@/lib/storage';
import { getSession, restDelete, restGet, restUpsert, supabaseConfigured } from '@/lib/supabase';
import type { Product, ProductAdminData } from '@/types/product';

export interface ProductRepository{
  list():Promise<Product[]>;
  getBySlug(slug:string):Promise<Product|undefined>;
  save(product:Product):Promise<Product>;
  remove(id:number):Promise<void>;
  reset():Promise<Product[]>;
}

const numberValue=(value:unknown,fallback=0)=>{
  const n=Number(value);
  return Number.isFinite(n)?n:fallback;
};

const normalizePublic=(raw:Product):Product=>({
  ...raw,
  cost:numberValue(raw.cost,0),
  metals:Array.isArray(raw.metals)?raw.metals:[],
  purities:Array.isArray(raw.purities)?raw.purities:[],
  gallery:Array.isArray(raw.gallery)?raw.gallery:[],
  hiddenImages:Array.isArray(raw.hiddenImages)?raw.hiddenImages:[],
  ringSizes:Array.isArray(raw.ringSizes)?raw.ringSizes:undefined,
  lengthOptions:Array.isArray(raw.lengthOptions)?raw.lengthOptions:undefined,
});

const legacyAdminData=(p:Product):ProductAdminData=>({
  supplier:p.admin?.supplier||p.source||'',
  supplierSku:p.admin?.supplierSku||'',
  supplierProductId:p.admin?.supplierProductId||(p.sourceId!=null?String(p.sourceId):''),
  supplierUrl:p.admin?.supplierUrl||p.sourceUrl||'',
  catalogPrice:numberValue(p.admin?.catalogPrice,0),
  supplierNet:numberValue(p.admin?.supplierNet,0),
  vatRate:numberValue(p.admin?.vatRate,18),
  supplierGross:numberValue(p.admin?.supplierGross,p.cost||0),
  priceMultiplier:numberValue(p.admin?.priceMultiplier,2),
  lastSyncedAt:p.admin?.lastSyncedAt||'',
  importBatchId:p.admin?.importBatchId||'',
  sourceImageUrls:p.admin?.sourceImageUrls||p.sourceImageUrls||[],
  sourceAttributes:p.admin?.sourceAttributes||p.sourceAttributes||[],
  sourceRaw:p.admin?.sourceRaw??p.sourceRaw,
});

const publicProduct=(p:Product):Product=>{
  const clean:any={...p};
  // Never persist supplier identity, acquisition cost or raw source data in the public product JSON.
  delete clean.cost;
  delete clean.admin;
  delete clean.source;
  delete clean.sourceId;
  delete clean.sourceUrl;
  delete clean.sourceImageUrls;
  delete clean.sourceAttributes;
  delete clean.sourceRaw;
  return clean as Product;
};

const mergeAdmin=(p:Product,admin?:ProductAdminData):Product=>{
  if(!admin)return normalizePublic(p);
  return {
    ...normalizePublic(p),
    cost:numberValue(admin.supplierGross,0),
    admin:{...admin,vatRate:numberValue(admin.vatRate,18),priceMultiplier:numberValue(admin.priceMultiplier,2)},
  };
};

export class LocalProductRepository implements ProductRepository{
  private key='products';
  async list(){return storage.get<Product[]>(this.key,initialProducts).map(normalizePublic)}
  async getBySlug(slug:string){return(await this.list()).find(p=>p.slug===slug)}
  async save(product:Product){const items=await this.list();const i=items.findIndex(p=>p.id===product.id);if(i>=0)items[i]=product;else items.unshift(product);storage.set(this.key,items);return product}
  async remove(id:number){storage.set(this.key,(await this.list()).filter(p=>p.id!==id))}
  async reset(){storage.set(this.key,initialProducts);return initialProducts}
}

export class SupabaseProductRepository implements ProductRepository{
  async list(){
    const rows=await restGet<{id:number;data:Product}[]>('luvera_products','select=id,data&order=id.asc');
    const products=(rows.length?rows.map(r=>normalizePublic(r.data)):initialProducts.map(normalizePublic));
    const session=await getSession();
    if(!session?.access_token)return products;
    try{
      const adminRows=await restGet<{product_id:number;data:ProductAdminData}[]>('luvera_product_admin','select=product_id,data');
      const byId=new Map(adminRows.map(r=>[Number(r.product_id),r.data]));
      return products.map(p=>mergeAdmin(p,byId.get(Number(p.id))));
    }catch{
      // Allows the storefront/admin to keep working before the V1 private-data migration is applied.
      return products;
    }
  }

  async getBySlug(slug:string){return(await this.list()).find(p=>p.slug===slug)}

  async save(product:Product){
    const admin=legacyAdminData(product);
    const pub=publicProduct(product);
    const session=await getSession();
    if(session?.access_token){
      // Fail before changing the public row if the private-table migration was not applied.
      try{await restGet('luvera_product_admin','select=product_id&limit=1')}
      catch{throw new Error('יש להריץ קודם את MIELLEY-CJ925-ADMIN-V1.sql ב-Supabase. המוצר לא שונה.')}
    }
    await restUpsert('luvera_products',{id:product.id,slug:product.slug,status:product.status,data:pub,updated_at:new Date().toISOString()});
    if(session?.access_token){
      await restUpsert('luvera_product_admin',{product_id:product.id,data:admin,updated_at:new Date().toISOString()},'product_id');
    }
    return mergeAdmin(pub,admin);
  }

  async remove(id:number){await restDelete('luvera_products',`id=eq.${id}`)}

  async reset(){
    const session=await getSession();
    if(session?.access_token){
      try{await restDelete('luvera_product_admin','product_id=gte.0')}catch{}
    }
    await restUpsert('luvera_products',initialProducts.map(p=>({id:p.id,slug:p.slug,status:p.status,data:publicProduct(p),updated_at:new Date().toISOString()})));
    return initialProducts;
  }
}

export const productRepository:ProductRepository=supabaseConfigured?new SupabaseProductRepository():new LocalProductRepository();
